/**
 * @file Root SPA shell: AuthProvider, router, nav, and route-level auth gates.
 * Public auth routes sit outside ProtectedRoute; app/admin/tools require session
 * and role/feature checks via ProtectedRoute props.
 * @module frontend/src/App
 */

import React, { Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, Search as NavSearchGlyph, X } from 'lucide-react';
import Home from './pages/Home';
import ViewBidders from './pages/ViewBidders';
import Login from './pages/Login';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { APP_HOME } from './constants';
import './App.css';

const Landing = React.lazy(() => import('./pages/Landing'));
const MergeData = React.lazy(() => import('./pages/MergeData'));
const HighVolumeReport = React.lazy(() => import('./pages/HighVolumeReport'));
const ScanItems = React.lazy(() => import('./pages/ScanItems'));
const NoBidsList = React.lazy(() => import('./pages/NoBidsList'));
const NoBidsScan = React.lazy(() => import('./pages/NoBidsScan'));
const Search = React.lazy(() => import('./pages/Search'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const AcceptInvite = React.lazy(() => import('./pages/AcceptInvite'));
const CompleteCompany = React.lazy(() => import('./pages/CompleteCompany'));
const AccountSettings = React.lazy(() => import('./pages/AccountSettings'));
const CompanySettings = React.lazy(() => import('./pages/CompanySettings'));
const AdminSettings = React.lazy(() => import('./pages/AdminSettings'));
const WebpToJpg = React.lazy(() => import('./pages/tools/WebpToJpg'));
const BackgroundRemover = React.lazy(() => import('./pages/tools/BackgroundRemover'));
const QrCodeGenerator = React.lazy(() => import('./pages/tools/QrCodeGenerator'));
const CategoryAssigner = React.lazy(() => import('./pages/tools/CategoryAssigner'));
const Inventory = React.lazy(() => import('./pages/tools/Inventory'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

/** Desktop Tools menu; image tools gated by canManageUsers; Category Assigner by subscription. */
function ToolsDropdown({ isActive, canManageUsers }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const location = useLocation();
  const { features } = useAuth();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`nav-dropdown ${open ? 'open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={isActive('/tools') ? 'nav-link active' : 'nav-link'}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        Tools
        <ChevronDown size={16} aria-hidden />
      </button>
      {open && (
        <div className="nav-dropdown-menu" role="menu">
          <Link
            role="menuitem"
            to="/tools/inventory"
            className={location.pathname.startsWith('/tools/inventory') ? 'nav-dropdown-item active' : 'nav-dropdown-item'}
            onClick={() => setOpen(false)}
          >
            Inventory Tagging
          </Link>
          {canManageUsers && (
            <>
              <Link
                role="menuitem"
                to="/tools/webp-to-jpg"
                className={location.pathname.startsWith('/tools/webp-to-jpg') ? 'nav-dropdown-item active' : 'nav-dropdown-item'}
                onClick={() => setOpen(false)}
              >
                WebP to JPG
              </Link>
              <Link
                role="menuitem"
                to="/tools/background-remover"
                className={location.pathname.startsWith('/tools/background-remover') ? 'nav-dropdown-item active' : 'nav-dropdown-item'}
                onClick={() => setOpen(false)}
              >
                Background Remover
              </Link>
              <Link
                role="menuitem"
                to="/tools/qr-code"
                className={location.pathname.startsWith('/tools/qr-code') ? 'nav-dropdown-item active' : 'nav-dropdown-item'}
                onClick={() => setOpen(false)}
              >
                QR Code Generator
              </Link>
            </>
          )}
          {features?.categoryAssigner && (
            <Link
              role="menuitem"
              to="/tools/category-assigner"
              className={location.pathname.startsWith('/tools/category-assigner') ? 'nav-dropdown-item active' : 'nav-dropdown-item'}
              onClick={() => setOpen(false)}
            >
              Category Assigner
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

/** Desktop Settings menu; company/admin links gated by role. */
function SettingsDropdown({ isActive, canManageUsers, isPlatformAdmin }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const location = useLocation();

  const settingsActive =
    location.pathname.startsWith('/company') ||
    location.pathname.startsWith('/account') ||
    location.pathname.startsWith('/admin');

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`nav-dropdown ${open ? 'open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={settingsActive ? 'nav-link active' : 'nav-link'}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        Settings
        <ChevronDown size={16} aria-hidden />
      </button>
      {open && (
        <div className="nav-dropdown-menu" role="menu">
          {canManageUsers && (
            <Link
              role="menuitem"
              to="/company"
              className={isActive('/company') ? 'nav-dropdown-item active' : 'nav-dropdown-item'}
              onClick={() => setOpen(false)}
            >
              Company Settings
            </Link>
          )}
          <Link
            role="menuitem"
            to="/account"
            className={isActive('/account') ? 'nav-dropdown-item active' : 'nav-dropdown-item'}
            onClick={() => setOpen(false)}
          >
            Account Settings
          </Link>
          {isPlatformAdmin && (
            <Link
              role="menuitem"
              to="/admin"
              className={isActive('/admin') ? 'nav-dropdown-item active' : 'nav-dropdown-item'}
              onClick={() => setOpen(false)}
            >
              Admin Settings
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Top nav for authenticated app chrome; hidden on landing, login, register, password, and invite.
 */
function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { canManageUsers, features, logout, isPlatformAdmin, canUseApp } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  const isActive = (path) => {
    if (path === APP_HOME) {
      return location.pathname === APP_HOME;
    }
    return location.pathname.startsWith(path);
  };

  const hideNav =
    location.pathname === '/' ||
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/forgot-password') ||
    location.pathname.startsWith('/reset-password') ||
    location.pathname.startsWith('/accept-invite');

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onDocClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  if (hideNav) return null;

  const closeMenu = () => setMenuOpen(false);

  const handleSignOut = () => {
    setMenuOpen(false);
    logout().then(() => navigate('/login'));
  };

  return (
    <nav className="navbar" ref={navRef}>
      <div className="container">
        <div className="nav-content">
          <div className="nav-bar-row">
            <Link to={APP_HOME} className="nav-brand" title="Home" onClick={closeMenu}>
              <img
                src={`${process.env.PUBLIC_URL || ''}/dictatel-bid_logo_transparent.png`}
                alt="Dictatel-Bid"
                className="nav-logo"
              />
              <h1 className="nav-title">Dictatel-Bid: Equip-Bid Auction Processor</h1>
            </Link>
            <button
              type="button"
              className="nav-menu-toggle"
              aria-expanded={menuOpen}
              aria-controls="nav-links-panel"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? <X size={24} aria-hidden /> : <Menu size={24} aria-hidden />}
            </button>
          </div>
          <div
            id="nav-links-panel"
            className={`nav-links${menuOpen ? ' is-open' : ''}`}
          >
            {canUseApp && (
            <>
            <Link
              to={APP_HOME}
              className={isActive(APP_HOME) ? 'nav-link active' : 'nav-link'}
              onClick={closeMenu}
            >
              Home
            </Link>
            <Link
              to="/search"
              className={location.pathname.startsWith('/search') ? 'nav-link active' : 'nav-link'}
              onClick={closeMenu}
            >
              <NavSearchGlyph size={18} aria-hidden />
              Search All Auctions
            </Link>

            <div className="nav-desktop-only">
              <ToolsDropdown isActive={isActive} canManageUsers={canManageUsers} />
            </div>
            </>
            )}
            <div className="nav-desktop-only">
              <SettingsDropdown
                isActive={isActive}
                canManageUsers={canManageUsers}
                isPlatformAdmin={isPlatformAdmin}
              />
            </div>

            {canUseApp && (
            <div className="nav-mobile-only nav-menu-section">
              <div className="nav-menu-section-label">Tools</div>
              <Link
                to="/tools/inventory"
                className={location.pathname.startsWith('/tools/inventory') ? 'nav-link active' : 'nav-link'}
                onClick={closeMenu}
              >
                Inventory Tagging
              </Link>
              {canManageUsers && (
                <>
                  <Link
                    to="/tools/webp-to-jpg"
                    className={location.pathname.startsWith('/tools/webp-to-jpg') ? 'nav-link active' : 'nav-link'}
                    onClick={closeMenu}
                  >
                    WebP to JPG
                  </Link>
                  <Link
                    to="/tools/background-remover"
                    className={location.pathname.startsWith('/tools/background-remover') ? 'nav-link active' : 'nav-link'}
                    onClick={closeMenu}
                  >
                    Background Remover
                  </Link>
                  <Link
                    to="/tools/qr-code"
                    className={location.pathname.startsWith('/tools/qr-code') ? 'nav-link active' : 'nav-link'}
                    onClick={closeMenu}
                  >
                    QR Code Generator
                  </Link>
                </>
              )}
              {features?.categoryAssigner && (
                <Link
                  to="/tools/category-assigner"
                  className={location.pathname.startsWith('/tools/category-assigner') ? 'nav-link active' : 'nav-link'}
                  onClick={closeMenu}
                >
                  Category Assigner
                </Link>
              )}
            </div>
            )}

            <div className="nav-mobile-only nav-menu-section">
              <div className="nav-menu-section-label">Settings</div>
              {canManageUsers && (
                <Link
                  to="/company"
                  className={isActive('/company') ? 'nav-link active' : 'nav-link'}
                  onClick={closeMenu}
                >
                  Company Settings
                </Link>
              )}
              <Link
                to="/account"
                className={isActive('/account') ? 'nav-link active' : 'nav-link'}
                onClick={closeMenu}
              >
                Account Settings
              </Link>
              {isPlatformAdmin && (
                <Link
                  to="/admin"
                  className={isActive('/admin') ? 'nav-link active' : 'nav-link'}
                  onClick={closeMenu}
                >
                  Admin Settings
                </Link>
              )}
            </div>

            <button
              type="button"
              className="nav-link nav-sign-out"
              onClick={handleSignOut}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}

/**
 * Route table: public auth flows, then ProtectedRoute-wrapped app surfaces.
 */
function AppContent() {
  return (
    <div className="App">
      <Navigation />
      <Suspense fallback={<LoadingSpinner label="Loading page..." />}>
        <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        {/* REVIEW [LOW]: Public route — ensure CompleteCompany requires an authenticated session server-side. */}
        <Route path="/complete-company" element={<CompleteCompany />} />

        <Route path="/" element={<Landing />} />
        <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/merge" element={<ProtectedRoute><MergeData /></ProtectedRoute>} />
        <Route path="/bidders" element={<ProtectedRoute><ViewBidders /></ProtectedRoute>} />
        <Route path="/bidders/:bidderId/scan" element={<ProtectedRoute><ScanItems /></ProtectedRoute>} />
        <Route path="/high-volume" element={<ProtectedRoute><HighVolumeReport /></ProtectedRoute>} />
        <Route path="/no-bids" element={<ProtectedRoute><NoBidsList /></ProtectedRoute>} />
        <Route path="/no-bids/scan" element={<ProtectedRoute><NoBidsScan /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute><Search /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
        <Route
          path="/company"
          element={
            <ProtectedRoute requireManageUsers>
              <CompanySettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requirePlatformAdmin>
              <AdminSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools/inventory"
          element={
            <ProtectedRoute>
              <Inventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools/webp-to-jpg"
          element={
            <ProtectedRoute requireManageUsers>
              <WebpToJpg />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools/background-remover"
          element={
            <ProtectedRoute requireManageUsers>
              <BackgroundRemover />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools/qr-code"
          element={
            <ProtectedRoute requireManageUsers>
              <QrCodeGenerator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools/category-assigner"
          element={
            <ProtectedRoute requireFeature="categoryAssigner">
              <CategoryAssigner />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
}

/**
 * App root: error boundary → auth context → client-side router.
 * @returns {JSX.Element}
 */
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
